import Status from "@/classes/Status";
import Product from "@/classes/Product";
import Tool from "@/classes/Tool";
import Organization from "@/classes/Organization"
import { UserService } from "@/classes/services/UserService";
import { ProductService } from "@/classes/services/ProductService";
import { objectEach } from "highcharts";

const productService = new ProductService();
const userService = new UserService();

const state = {
  selectedReportOrg: new Organization(),
  statuses: [],
  products: [],
  tools: [],
  productTools: [],
  timeFrame: [],
  productToolsSessionCount: [],
  sessionCounts: [],
  distinctUserCounts: [],
  concurrentProductSessionCount: [],
  isGraphLoading: false,
  appSessionsDataByProduct: [],
  selectedOrgProducts: []
};

const getters = {
  getProducts() {
    return state.products;
  },

  getAppSessionsDataByProduct() {
    return state.appSessionsDataByProduct
  },

  getSelectedReportOrg() {
    return state.selectedReportOrg;
  },

  getTools() {
    return state.tools;
  },

  getStatusList() {
    return state.statuses;
  },

  getProductTools() {
    return state.productTools;
  },

  getProductToolsSessionCount() {
    return state.productToolsSessionCount;
  },
  
  getSessionCounts() {
    return state.sessionCounts;
  },

  getDistinctUserCounts() {
    return state.distinctUserCounts;
  },

  getConcurrentProductSessionCount() {
    return state.concurrentProductSessionCount;
  },

  getSelectedOrgProducts() {
    return state.selectedOrgProducts
  },

  getIsGraphLoading() {
    return state.isGraphLoading;
  },
};

const mutations = {
  setAppSessionsDataByProduct(state, payload) {
    state.appSessionsDataByProduct = payload;
  },
  setSelectedReportOrg(state, payload) {
    state.selectedReportOrg = payload;
  },
  setIsGraphLoading(state, payload) {
    state.isGraphLoading = payload;
  },
  setProductTools(state, payload) {
    state.productTools = payload;
  },
  setProductToolsSessionCount(state, payload) {
    return (state.productToolsSessionCount = payload);
  },
  setSessionCounts(state, payload) {
    return (state.sessionCounts = payload);
  },
  setDistinctUserCounts(state, payload) {
    return (state.distinctUserCounts = payload);
  },
  setConcurrentProductSessionCount(state, payload) {
    return (state.concurrentProductSessionCount = payload);
  },
  setSelectedOrgProducts(state, payload) {
    return state.selectedOrgProducts = payload;
  }
};

const actions = {
  async getProductsListAsync({ rootState, commit, dispatch }) {
    productService.getProducts().then((response: any) => {
      state.products = response.data.items.map((datum: any) => {
        return new Product(datum.id, datum.name);
      });
          dispatch("getLatestProductVersionwithToolsAsync");  
    });
    
  },

  async getToolsListAsync() {
    productService.getTools().then((response: any) => {
      state.tools = response.data.items.map((datum: any) => {
        return new Tool(datum.id, datum.name);
      });
    });
  },
  

  async getStatusListAsync() {
    userService
      .getUserStatuses()
      .then((response: any) => {
        state.statuses = response.data.map((datum: any) => {
          return new Status(datum.displayName, datum.intValue);
        });
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });
  },

  async getLatestProductVersionwithToolsAsync(
    { rootState, commit, dispatch }
  ) {
    let productTools = [];
    state.products.forEach(p => {
      productService
      .getProductLatestVersionwithTools(p.id)
      .then((response: any) => {
        var tools = response.data.tools.map(t => t.id)
        productTools.push({ productId: p.id, toolIds: tools })
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });
    });
    
    commit("setProductTools", productTools);
  },

  async getProductToolsSessionsCountAsync(
    { rootState, commit, dispatch },
    payload
  ) {
    commit("setIsGraphLoading", true);
    //dispatch("getLatestProductVersionwithToolsAsync", payload.productId);
    productService
      .getProductToolsSessionsCount(payload.productId, payload.timeFrame)
      .then((response: any) => {
        const toolSessionArray = [];
        Object.keys(response.data).forEach((key) => {
          toolSessionArray.push([key, response.data[key]]);
        });
        commit("setProductToolsSessionCount", toolSessionArray);
        commit("setIsGraphLoading", false);
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });
  },

  async getSelectedOrgProducts(
    { rootState, commit, dispatch },
    payload
  ) {
    commit("setIsGraphLoading", true);
    productService
      .getSelectedOrgProducts(payload)
      .then((response: any) => {
        var productIds = response.data.map(datum => datum.productId)
        commit("setSelectedOrgProducts", productIds);
        commit("setIsGraphLoading", false);
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });
  },

    async getSessionsCountsAsync(
    { rootState, commit, dispatch },
    payload
  ) {
    commit("setIsGraphLoading", true);
    let reqObj: { organizationId: string, productIds: Array, toolIds: Array};

    reqObj = {
      organizationId: payload?.organizationId ?? "",
      productIds: payload?.productIds ?? [],
      toolIds: payload?.toolIds ?? []
    };
    productService
      .getSessionCounts(payload?.timeFrame ?? 30, reqObj)
      .then((response: any) => {
        commit("setSessionCounts", response.data);
        commit("setIsGraphLoading", false);
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });

    productService
      .getDistinctUserCounts(payload?.timeFrame ?? 30, reqObj)
      .then((response: any) => {
        commit("setDistinctUserCounts", response.data);
        commit("setIsGraphLoading", false);
      })
      .catch(() => {
        // Errors are automatically logged in app insights
      });
  },

    async getAppSessionsDataByProduct(
      { dispatch, commit }, payload) {
      commit("setIsGraphLoading", true);

      productService
      .getAppSessionsDataByProduct(
        payload.organizationId,
        payload.productId,
        payload.days
      )
      .then((response: any) => {
        commit("setAppSessionsDataByProduct", response.data)   
        commit("setIsGraphLoading", false);
      })
      .catch((error: AxiosError<Error>) => {
      });
    },

  async getConcurrencyLicenseUsageChartData({ dispatch, commit }, payload) {
    productService
      .getConcurrencyLicenseUsageChartData(
        payload.organizationId,
        payload.productIds
      )
      .then((response: any) => {
        const concurrentSessionArray = [];

        Object.keys(response.data).forEach((i) => {
          var datum = response.data[i]
          let count = 0
          switch (payload.days) {
            case 30:
              count = datum.concurrencyCountLast30Days
              break;
              case 60:
                count = datum.concurrencyCountLast60Days
              break;            
              case 90:
                count = datum.concurrencyCountLast90Days
              break;            
              case 365:
                count = datum.concurrencyCountLast365Days
              break;
            default:
              break;
          }

          var productId = datum.productId

          var name = state.products.find(p => p.id === productId).name
          concurrentSessionArray.push({ productId, name, count });
        });
        commit("setConcurrentProductSessionCount", concurrentSessionArray);
        commit("setIsGraphLoading", false);
      })
      .catch((error: AxiosError<Error>) => {
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
